/**
 * Sums multiple properties of an array of objects
 *
 * Type Safety: The function is fully typed with generics:
 * T represents the object type in the array
 * K represents the keys of T that you want to sum
 * Flexible: Can sum any number of properties in a single pass
 * Robust: Safely handles non-numeric values by converting to Number and checking for NaN
 * Variadic Parameters: Accepts any number of property names to sum
 *
 * Usage: sumProperties(products, "price", "quantity");
 *
 * @param  {T[]} arr
 * @param  {K[]} ...properties
 * @returns Record
 */
export function sumProperties<T extends Record<string, any>, K extends keyof T>(arr: T[], ...properties: K[]): Record<K, number> {
  // Initialize the result object with 0 for each property
  const result = {} as Record<K, number>;
  for (const prop of properties) {
    result[prop] = 0;
  }

  // Sum each property across all objects
  for (const obj of arr) {
    for (const prop of properties) {
      const value = Number(obj[prop]);
      if (!isNaN(value)) {
        result[prop] += value;
      }
    }
  }

  return result;
}

/**
 * Sums single property of an array of objects
 *
 * Type Safety: The function is fully typed with generics:
 * T represents the object type in the array
 * K represents the key of T that you want to sum
 * Robust: Safely handles non-numeric values by converting to Number and checking for NaN
 *
 * Usage: sumProperties(products, "price", "quantity");
 * @param  {T[]} arr
 * @param  {keyofT} property
 * @returns number
 */
export function sumProperty<T extends Record<string, any>>(arr: T[], property: keyof T): number {
  return arr.reduce((sum, obj) => {
    const value = Number(obj[property]);
    return sum + (isNaN(value) ? 0 : value);
  }, 0);
}
